import { createEntityAdapter, createAction, createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit'
import orderBy from 'lodash/orderBy'
import ActionTypes from '@constants/action_types.js'
import { RootState } from '@store/store'
import { FieldSelectOption } from '@app-types/entity_types'
import { createLogger } from '@utils/logger'

const log = createLogger('field_select_option reducer')
log.setLevel('info')

const receiveEntities = createAction<{ field_select_options?: { [id: number]: FieldSelectOption } }>(
  ActionTypes.RECEIVE_ENTITIES
)

export const fieldSelectOptionsAdapter = createEntityAdapter<FieldSelectOption>()

// initial state derived from the adapter
const initialState = fieldSelectOptionsAdapter.getInitialState()

const slice = createSlice({
  name: 'fieldSelectOptions',
  initialState,
  reducers: {},
  extraReducers: builder => {

    // handle receiving entities action to add/update/remove field select options
    builder.addCase(
      receiveEntities,
      (
        state,
        action: PayloadAction<{ field_select_options?: { [id: number]: FieldSelectOption | null } }>
      ) => {
        const optionsObj = action.payload.field_select_options
        if (optionsObj && Object.keys(optionsObj).length) {
          // we have at least one option to add or remove

          const toRemove: number[] = []
          const toUpsert: FieldSelectOption[] = []
          Object.entries(optionsObj).forEach(([id, value]) => {
            if (value === null) {
              toRemove.push(Number(id))
            } else {
              toUpsert.push(value)
            }
          })

          if (toUpsert.length) {
            // add or update options
            log.debug('Upserting field select options:', toUpsert)
            fieldSelectOptionsAdapter.upsertMany(state, toUpsert)
          }
          if (toRemove.length) {
            // remove options
            log.debug('Removing field select options:', toRemove)
            fieldSelectOptionsAdapter.removeMany(state, toRemove.map(String))
          }
        }
      }
    )
  },
})

// Selectors

export const fieldSelectOptionsSelectors = fieldSelectOptionsAdapter.getSelectors(
  (state: RootState) => state.data.fieldSelectOptions
)

export const selectFieldSelectOptionsByFieldId = createSelector(
  [
    (state: RootState) => fieldSelectOptionsSelectors.selectAll(state),
    (_state: RootState, fieldId: string) => fieldId,
  ],
  (options, fieldId) => {
    return orderBy(
      options.filter(option => option.attributes.field_id.toString() === fieldId),
      o => o.attributes.position,
      'asc'
    )
  }
)

export default slice.reducer
